import { useCallback, useEffect, useMemo } from "react";
import type { Route } from "../types";
import { getJourneyState, type JourneyStop } from "../lib/journey";
import { navigate } from "../lib/navigation";
import { usePortfolioStore } from "../state/usePortfolioStore";

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) ||
    target.getAttribute("role") === "textbox"
  );
}

export function useJourneyNavigation(route: Route) {
  const selectedAiSection = usePortfolioStore(
    (state) => state.selectedAiSection,
  );
  const setAiSection = usePortfolioStore(
    (state) => state.setAiSection,
  );
  const selectedPillarPrinciples = usePortfolioStore(
    (state) => state.selectedPillarPrinciples,
  );
  const setPillarPrinciple = usePortfolioStore(
    (state) => state.setPillarPrinciple,
  );
  const state = useMemo(
    () =>
      getJourneyState(
        route,
        selectedPillarPrinciples,
        selectedAiSection,
      ),
    [route, selectedAiSection, selectedPillarPrinciples],
  );

  const activate = useCallback(
    (stop: JourneyStop | null) => {
      if (!stop) return;

      if (stop.aiSectionId) {
        setAiSection(
          stop.aiSectionId === "overview" ? null : stop.aiSectionId,
        );
      }
      if (stop.pillarId) {
        setPillarPrinciple(
          stop.pillarId,
          stop.pillarPrincipleId ?? null,
        );
      }

      const currentPath =
        route.kind === "home"
          ? "/"
          : route.kind === "workflow"
            ? "/workflow"
            : route.kind === "pillar"
              ? `/pillar/${route.slug}`
              : `/${route.kind}`;

      if (currentPath !== stop.route) {
        navigate(stop.route);
        return;
      }

      if (
        !stop.evidence &&
        ((stop.aiSectionId && stop.aiSectionId !== "overview") ||
          stop.pillarPrincipleId)
      ) {
        requestAnimationFrame(() => {
          document
            .querySelector(
              stop.aiSectionId
                ? ".ai-principle-focus"
                : ".pillar-principle-focus",
            )
            ?.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
              .matches
              ? "auto"
              : "smooth",
            block: "nearest",
          });
        });
      }
    },
    [route, setAiSection, setPillarPrinciple],
  );

  const goPrevious = useCallback(
    () => activate(state?.previous ?? null),
    [activate, state?.previous],
  );
  const goNext = useCallback(
    () => activate(state?.next ?? null),
    [activate, state?.next],
  );

  useEffect(() => {
    if (!state) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        isEditableTarget(event.target)
      ) {
        return;
      }

      if (event.key === "ArrowLeft" && state.previous) {
        event.preventDefault();
        goPrevious();
      }
      if (event.key === "ArrowRight" && state.next) {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goNext, goPrevious, state]);

  return {
    state,
    goPrevious,
    goNext,
  };
}
